import { CompletedWorkoutFilter, WorkoutFilter } from 'src/service/core-service'

export const WORKOUTS_QUERY_KEY = 'workouts'

const workoutKeys = {
  workouts: (filter: WorkoutFilter | undefined) => [WORKOUTS_QUERY_KEY, filter] as const,
  workoutsWithMeta: (filter: WorkoutFilter | undefined) => [WORKOUTS_QUERY_KEY, 'with-meta', filter] as const,
  workout: (id: string) => [WORKOUTS_QUERY_KEY, id] as const,
  popularWorkouts: () => [WORKOUTS_QUERY_KEY, 'popular'] as const,
  newWorkouts: () => [WORKOUTS_QUERY_KEY, 'new-workouts'] as const,
  favoriteWorkouts: () => [WORKOUTS_QUERY_KEY, 'favorite'] as const,
  trendingExpressWorkouts: () => [WORKOUTS_QUERY_KEY, 'trending-express'] as const,
  coreChargeWorkouts: () => [WORKOUTS_QUERY_KEY, 'core_charge'] as const,
  flexFlowWorkouts: () => [WORKOUTS_QUERY_KEY, 'flex_flow'] as const,
  completedWorkouts: (filter?: CompletedWorkoutFilter) => [WORKOUTS_QUERY_KEY, 'completed', filter] as const,
  workoutCompletedBy: (workoutId: string, offset: number) => [WORKOUTS_QUERY_KEY, workoutId, 'completed-by', offset] as const,
  todayWorkouts: () => [WORKOUTS_QUERY_KEY, { preset: 'today' }] as const,
  suggestedWorkouts: () => [WORKOUTS_QUERY_KEY, 'suggested'] as const,
  featuredWorkouts: () => [WORKOUTS_QUERY_KEY, 'featured'] as const,
  completedWorkoutsInRange: (startDate: number, endDate: number) =>
    [WORKOUTS_QUERY_KEY, { dateRange: [startDate, endDate] }] as const,
  workoutEquipmentGroups: () => ['workoutEquipmentGroups'] as const,
  workoutDurations: () => ['workoutDurations'] as const,
  workoutIntensities: () => ['workoutIntensities'] as const,
  workoutStyles: () => ['workoutStyles'] as const,
  workoutTypes: () => ['workoutTypes'] as const,
  workoutIntensity: (id: string) => [WORKOUTS_QUERY_KEY, id] as const,
}

const workoutStatsKeys = {
  currentMonthWorkoutStats: () => ['currentMonthWorkoutStats'] as const,
}

const weeklyImageKeys = {
  weeklyImage: (dayInWeek: number) => ['weeklyImage', dayInWeek] as const,
}

const trainingSeriesKeys = {
  runningSeries: () => ['runningSeries'] as const,
  trainingSeriesById: (id: string) => ['trainingSeries', id] as const,
}

const trainerKeys = {
  trainers: () => ['trainers'] as const,
  trainer: (id: string) => ['trainers', id] as const,
}

const thisWeekPromoVideoKeys = {
  thisWeekPromoVideo: () => ['thisWeekPromoVideo'] as const,
}

const educationKeys = {
  educationHubCategories: () => ['educationHubCategories'] as const,
}

const programmeKeys = {
  programmes: () => ['programmes', 'all'] as const,
  programme: (programmeId: string) => ['programmes', programmeId] as const,
  myStartedProgrammes: () => ['programmes', 'started'] as const,
  myStartedProgrammesFromLibrary: (params: { page: number; pageSize: number }) =>
    ['programmes', 'started', 'library', { page: params.page, pageSize: params.pageSize }] as const,
  /** Prefix for `invalidateQueries` — matches all library started-programme pages */
  myStartedProgrammesFromLibraryPrefix: () => ['programmes', 'started', 'library'] as const,
  myCompletedProgrammes: () => ['programmes', 'completed'] as const,
  suggestedSeries: () => ['programmes', 'suggested-series'] as const,
  programmeEquipment: (programmeId: string) => ['programmes', programmeId, 'equipment'] as const,
  programmeDuration: (programmeId: string) => ['programmes', programmeId, 'duration'] as const,
  programmeIntensities: () => ['programmes', 'intensities'] as const,
  programmeWorkouts: (programmeId: string) => ['programme-workout', programmeId] as const,
  completedProgrammeWorkouts: (programmeId: string) => ['programme-workout', programmeId, 'completed'] as const,
  programmeWorkout: (programmeWorkoutId: string) => ['programme-workout', programmeWorkoutId] as const,
  programmeWorkoutsAvailability: (programmeId: string) => ['programme-workout', programmeId, 'availability'] as const,
  hustlerProgramme: (programmeId: string) => ['hustler-programme', programmeId] as const,
  availableProgrammeAwards: (programmeId: string) => ['programme-awards', programmeId, 'available'] as const,
  programmeAwardsByProgramme: (programmeId: string) => ['programme-awards', programmeId, 'user'] as const,
}

const legalKeys = {
  legalPrivacyPolicy: () => ['legal', 'privacy-policy'] as const,
  legalTermsAndConditions: () => ['legal', 'terms-and-conditions'] as const,
}

const challengeKeys = {
  completedChallenges: () => ['completedChallenges'] as const,
  notYetCompletedChallenges: () => ['notYetCompletedChallenges'] as const,
  programmeAwards: () => ['programmeAwards'] as const,
}

const bodyPartKeys = {
  bodyParts: () => ['bodyParts'] as const,
}

const imageKeys = {
  imageFile: (fileId: string) => ['imageFile', fileId] as const,
}

const communityKeys = {
  comments: (entityType: string, entityId: string, sortBy?: string) =>
    ['community', 'comments', entityType, entityId, sortBy] as const,
  commentReplies: (commentId: string) => ['community', 'comments', commentId, 'replies'] as const,
  reactions: (entityType: string, entityId: string) => ['community', 'reactions', entityType, entityId] as const,
  reactionTypes: () => ['community', 'reaction-types'] as const,
  adminComments: (params?: { offset?: number; search?: string; entityType?: string }) =>
    ['admin-comments', params?.offset, params?.search, params?.entityType] as const,
  adminComment: (commentId: string) => ['admin-comments', commentId] as const,
}

const messageKeys = {
  emailNotifications: () => ['emailNotifications'] as const,
}

const membershipKeys = {
  mySubscriptions: () => ['membership', 'mySubscriptions'] as const,
  detailedSubscriptions: () => ['membership', 'detailedSubscriptions'] as const,
  prices: () => ['membership', 'prices'] as const,
}

const authKeys = {
  requestPasswordlessLogin: () => ['auth', 'requestPasswordlessLogin'] as const,
  verifyPasswordlessLogin: () => ['auth', 'verifyPasswordlessLogin'] as const,
  resendPasswordlessCode: () => ['auth', 'resendPasswordlessCode'] as const,
  signInWithGoogle: () => ['auth', 'signInWithGoogle'] as const,
  signInWithApple: () => ['auth', 'signInWithApple'] as const,
}

const groupKeys = {
  discoverGroups: (search?: string) => ['groups', 'discover', search] as const,
  myGroups: () => ['groups', 'me'] as const,
  group: (groupId: string) => ['groups', groupId] as const,
  groupMembers: (groupId: string) => ['groups', groupId, 'members'] as const,
  groupPosts: (groupId: string) => ['groups', groupId, 'posts'] as const,
  adminGroups: (offset?: number, search?: string) => ['admin-groups', offset, search] as const,
  adminGroup: (groupId: string) => ['admin-groups', groupId] as const,
  adminGroupMembers: (groupId: string, offset?: number) => ['admin-groups', groupId, 'members', offset] as const,
  adminGroupPosts: (groupId: string, offset?: number) => ['admin-groups', groupId, 'posts', offset] as const,
}

export const queryKeys = {
  ...authKeys,
  ...workoutKeys,
  ...workoutStatsKeys,
  ...weeklyImageKeys,
  ...trainingSeriesKeys,
  ...trainerKeys,
  ...thisWeekPromoVideoKeys,
  ...programmeKeys,
  ...legalKeys,
  ...educationKeys,
  ...challengeKeys,
  ...bodyPartKeys,
  ...imageKeys,
  ...communityKeys,
  ...messageKeys,
  ...membershipKeys,
  ...groupKeys,
}
